import React from "react";
import { faFileSignature } from "@fortawesome/pro-regular-svg-icons";
import {
    getPackageUrl,
    getSignatureUrl,
} from "@plugins/gatsby-source-paligo/src/helpers/release";
import { Flex } from "@singlestore/fusion/components/layout";
import { useVerifySignature } from "contexts/VerifySignatureContext";

import { Button } from "components/Button/Button";
import { LinkButton } from "components/Button/LinkButton";
import {
    DownloadButton,
    DownloadButtonProps,
} from "components/DownloadButton/DownloadButton";
import { FaIcon } from "components/FaIcon/FaIcon";

import { track } from "utils/segment";

type PackageButtonProps = {
    target: string;
    releaseJson: any;
    releaseUrl: string;
} & Omit<DownloadButtonProps, "to" | "signatureUrl">;

export function PackageButton({
    target,
    releaseJson,
    releaseUrl,
    onClick,
    ...rest
}: PackageButtonProps) {
    const { setSelected } = useVerifySignature();

    const packageObj = React.useMemo(() => {
        return releaseJson?.packages?.[target];
    }, [releaseJson, target]);

    const packageUrl = React.useMemo(() => {
        if (!packageObj) {
            return undefined;
        }

        return getPackageUrl(packageObj.Path);
    }, [packageObj]);

    const signatureUrl = React.useMemo(() => {
        if (!packageObj?.Signature) {
            return undefined;
        }

        return getSignatureUrl(packageObj.Signature);
    }, [packageObj]);

    const handleInstructionClick = () => {
        setSelected({ target, releaseJson, releaseUrl });
    };

    const handleDownloadClick: React.ComponentProps<
        typeof DownloadButton
    >["onClick"] = (event) => {
        if (target) {
            track("Package Downloaded", {
                target,
                packagePath: releaseJson?.packages?.[target]?.path,
            });
        }

        onClick?.(event);
    };

    let signatureButtons;
    if (signatureUrl) {
        const signatureIcon = <FaIcon icon={faFileSignature} />;

        signatureButtons = (
            <Flex flexDirection="column" gap="0-5x">
                <LinkButton
                    iconLeft={signatureIcon}
                    variation="secondary"
                    aria-label="Download"
                    to={signatureUrl}
                >
                    Download signature
                </LinkButton>

                <Button
                    size="sm"
                    variation="plain"
                    onClick={handleInstructionClick}
                >
                    How do I verify this signature?
                </Button>
            </Flex>
        );
    }

    if (!packageUrl) {
        return null;
    }

    return (
        <Flex
            flexDirection="column"
            alignItems="start"
            className="download-button-wrapper"
            gap="1x"
        >
            <DownloadButton
                {...rest}
                to={packageUrl}
                onClick={handleDownloadClick}
            />
            {signatureButtons}
        </Flex>
    );
}
